/* eslint-disable operator-assignment */
import { Fragment, startTransition, useEffect, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { Query, type QueryKey } from "@tanstack/react-query"
import { AxiosError } from "axios"
import eq from "lodash-es/eq"
import slice from "lodash-es/slice"
import { useMap, useUnmount, useUpdateEffect } from "react-use"
import type { StableActions } from "react-use/lib/useMap"

import { InfoBox, Loadable } from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { useCoachGroupSessionQueries as useQueries } from "./coach.group.api.queries.hooks"
import type {
    ICoachGroupSessionSummaryDto as ISummaryDto,
    ICoachGroupSessionSummaryModel as ISummaryModel
} from "./coach.group.api.types"
import SectionHeading from "./coach.group.component.section-heading"
import Summaries from "./coach.group.component.summaries"
import mocks from "./coach.group.mocks"
import styles from "./coach.group.styles"
import uiConfig from "./coach.group.ui.config"
import uiHooks from "./coach.group.ui.hooks"

const {
    entities: { summary: classes }
} = styles

const {
    SUMMARY_LOADING_ARTIFICIAL_DELAY_IN_MS: DELAY_TIME,
    SUMMARY_MAX_REFETCH_COUNT: REFETCH_COUNT,
    SUMMARY_REFETCH_INTERVAL_IN_MS: INTERVAL_TIME
} = uiConfig

type TProps = { isAvailable: boolean; withWarningOnAbsence: boolean }

/**
 * Coach "GroupSession page"' module summary
 * Design spec {@link https://www.figma.com/design/QLhX4KcIViCdMD12xdfvXq/Coach?node-id=3022-45351&m=dev}
 *
 * @return ReactElement
 */
const CoachGroupSummaryEntity: FC<TProps> = ({ isAvailable, withWarningOnAbsence }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const queryCountRef: RefObject<number> = useRef<number>(0)

    const isQueryEnabled: boolean = uiHooks.useQueryDelayedEnabled(
        isAvailable && queryCountRef.current < REFETCH_COUNT,
        DELAY_TIME
    )

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const [summaryData, { set: updateSummaryProp }]: [ISummaryModel, StableActions<ISummaryModel>] =
        useMap<ISummaryModel>(
            breakpoints.xs || breakpoints.sm
                ? {
                      id: mocks.models.summary.id,
                      summaries: slice(mocks.models.summary.summaries, 0, 3)
                  }
                : mocks.models.summary
        )

    const {
        summary: { data: fetchedData, isStale, isFetched, dataUpdatedAt: fetchedDataUpdatedAt }
    }: ReturnType<typeof useQueries> = useQueries({
        summary: {
            enabled: isQueryEnabled,
            refetchInterval: (query: Query<ISummaryDto, AxiosError, ISummaryModel, QueryKey>): number | false =>
                !query.state.data?.id ? INTERVAL_TIME : false
        }
    })

    useEffect((): void => {
        if (fetchedDataUpdatedAt === 0) queryCountRef.current = 1
        else queryCountRef.current = queryCountRef.current + 1
    }, [isFetched, fetchedDataUpdatedAt])

    useUpdateEffect((): void => {
        if ((isFetched || isStale) && !eq(fetchedData?.id, -1) && fetchedData?.id)
            startTransition(
                (): void => (
                    updateSummaryProp("id", fetchedData.id), updateSummaryProp("summaries", fetchedData.summaries)
                )
            )
    }, [isFetched, isStale, fetchedData])

    useUnmount((): void => {
        if (isFetched) {
            updateSummaryProp("id", summaryData.id)
            updateSummaryProp("summaries", summaryData.summaries)
        }
    })

    return (
        <section className={classes.container({ isVisible: isAvailable || withWarningOnAbsence })}>
            {isAvailable ? (
                <Fragment>
                    <SectionHeading type="summary" summaryId={!eq(summaryData.id, -1) ? summaryData.id : undefined} />

                    <Loadable
                        component={<Summaries summaries={summaryData.summaries} isLoading={eq(summaryData.id, -1)} />}
                        isReady
                        withoutSuspense
                    />
                </Fragment>
            ) : withWarningOnAbsence ? (
                <InfoBox
                    variant="warning"
                    title={t("coachSide.groupPage.summary.warning.na.title")}
                    message={t("coachSide.groupPage.summary.warning.na.message")}
                />
            ) : null}
        </section>
    )
}

CoachGroupSummaryEntity.displayName = "CoachGroupSummaryEntity"

export default CoachGroupSummaryEntity
